import React, { useState } from 'react';
import { Mail, Send, Share2, Copy, Check, MessageCircle, Ghost, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '../lib/api';
import { useT } from '../context/LanguageContext';
import { SITE_URL } from './Seo';

const shareText = 'Refuel Weekend 2027 – 2.–4. april i Hjeltefjorden Arena, Øygarden!';

const Newsletter = () => {
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/newsletter', { email });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const copyLink = async (which = 'instagram') => {
    try {
      await navigator.clipboard.writeText(SITE_URL);
      setCopied(which);
      toast.success(t('Lenka er kopiert – lim ho inn i Instagram!'));
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.info(SITE_URL);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'Refuel Weekend 2027', text: shareText, url: SITE_URL }); } catch { /* cancelled */ }
    } else {
      copyLink('native');
    }
  };

  const shareLinks = [
    { id: 'snapchat', label: 'Snapchat', Icon: Ghost, href: `https://www.snapchat.com/scan?attachmentUrl=${encodeURIComponent(SITE_URL)}`, cls: 'bg-[#FFFC00] text-black' },
    { id: 'messenger', label: 'Messenger', Icon: MessageCircle, href: `fb-messenger://share/?link=${encodeURIComponent(SITE_URL)}`, cls: 'bg-gradient-to-r from-[#00B2FF] to-[#A033FF] text-white' },
  ];

  return (
    <section id="nyheitsbrev" className="bg-black py-20 md:py-24 border-t border-white/5" data-testid="newsletter-section">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 rounded-3xl bg-[#1a1424] border border-white/5 p-7 md:p-10">
          <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-sm mb-2 inline-flex items-center gap-2"><Mail size={14} /> {t('Nyheitsbrev')}</p>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tighter mb-3" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t('Få siste nytt om Refuel Weekend')}</h2>
          <p className="text-white/60 mb-6">{t('Vi sender deg program, artistar og påmeldingsfristar – ingen spam.')}</p>
          {done ? (
            <div data-testid="newsletter-success" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500/15 text-emerald-300 font-bold"><Check size={18} /> {t('Takk! Du er påmeldt nyheitsbrevet.')}</div>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
              <input data-testid="newsletter-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="deg@epost.no" className="flex-1 px-5 py-3.5 rounded-full bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#ff2e2e] focus:outline-none" />
              <button type="submit" disabled={sending} data-testid="newsletter-submit-button" className="px-7 py-3.5 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {t('Meld meg på')} <Send size={16} />
              </button>
            </form>
          )}
        </div>

        <div className="lg:col-span-2 rounded-3xl bg-[#0f0a17] border border-white/10 p-7 md:p-10 flex flex-col" data-testid="share-card">
          <p className="text-white/50 font-bold uppercase tracking-widest text-sm mb-2 inline-flex items-center gap-2"><Share2 size={14} /> {t('Del nettsida')}</p>
          <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-5" style={{ fontFamily: "'Oktah Round', sans-serif" }}>refuel.rest</h3>
          <div className="grid grid-cols-2 gap-3 mt-auto">
            {shareLinks.map(({ id, label, Icon, href, cls }) => (
              <a key={id} href={href} target="_blank" rel="noreferrer noopener" data-testid={`share-${id}`} className={`inline-flex items-center justify-center gap-2 h-12 rounded-full font-bold text-sm hover:scale-[1.03] transition-transform ${cls}`}>
                <Icon size={18} /> {label}
              </a>
            ))}
            <button onClick={() => copyLink('instagram')} data-testid="share-instagram" className="inline-flex items-center justify-center gap-2 h-12 rounded-full font-bold text-sm bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 text-white hover:scale-[1.03] transition-transform">
              {copied === 'instagram' ? <Check size={18} /> : <Instagram size={18} />} Instagram
            </button>
            <button onClick={nativeShare} data-testid="share-native" className="inline-flex items-center justify-center gap-2 h-12 rounded-full font-bold text-sm border border-white/20 text-white hover:border-[#ff2e2e] hover:text-[#ff2e2e] transition-colors">
              {copied === 'native' ? <Check size={18} /> : <Copy size={18} />} {navigator.share ? t('Del…') : t('Kopier lenke')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
